import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-700 mb-2">文章不存在</h2>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回文章列表</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>{`${article.title} - 草莓导航`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`https://strawberry-nav.com/article/${slug}`} />
      <meta property="og:title" content={`${article.title} - 草莓导航`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://strawberry-nav.com/article/${slug}`} />
      <meta property="article:published_time" content={article.date} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - 草莓导航`} />
      <meta name="twitter:description" content={article.excerpt} />

      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          publisher: { '@type': 'Organization', name: '草莓导航' },
          inLanguage: 'zh-CN',
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://strawberry-nav.com' },
            { '@type': 'ListItem', position: 2, name: '推荐文章', item: 'https://strawberry-nav.com/articles' },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '推荐文章', to: '/articles' },
          { label: article.title },
        ]} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime} 阅读</span>
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-a:text-primary-600 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group p-5 rounded-2xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700 mb-3">
                      {ra.categoryName}
                    </span>
                    <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {ra.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}